// TabContent1.js
import { useTheme } from '@emotion/react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, ButtonBase } from '@mui/material';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useGetShippersSelectQuery } from '../../../../../newapi/shipper/shipperSlice';
import { useGetWarehousesSelectQuery } from '../../../../../newapi/warehouse/warehouseApiSlice';
import { tokens } from '../../../../../theme';
import { countriesFromLocale, regions } from '../../../../../utils/countries';
import { yupRequired } from '../../../../../utils/validation';
import SHQuickAction from '../../../../theme/Modal/SHQuickAction';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import Subheading from '../../../../theme/text/Subheading';
import Text from '../../../../theme/text/Text';

/**
 * TabContent1 Component
 *
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const [fieldErrors, setFieldErrors] = useState({});
    const dispatch = useDispatch();
    const [shipper, setShipper] = useState(props.shipper);
    const { data: shippers } = useGetShippersSelectQuery({includeBol: true});
    const { data: warehouses } = useGetWarehousesSelectQuery(undefined, { skip: props.isReturnProfile });

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    // Set allowReturn to true when isReturnProfile is true
    useEffect(() => {
        if (props.isReturnProfile && !data.allowReturn) {
            props.handleDataUpdate({ allowReturn: true });
        }
    }, [props.isReturnProfile]);

    const handleChange = (key, val) => {
        if(key === "shipperId") {
            const selectedShipper = shippers.find(shipper => shipper.id === val);
            const shipperType = selectedShipper ? selectedShipper.type : null;
            if(shipperType){
                props.handleDataUpdate({ shipperType: shipperType });
            }
        }
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const handleCountryChange = (selectedIndices) => {
        if (!countries) return;

        const selectedCountryCodes = selectedIndices.map(index => countries[index].alpha2);
        const returnOptions = {
            ...(data.returnOptions || {}),
            countries: selectedCountryCodes
        };
        props.handleDataUpdate({ returnOptions: returnOptions });
    };

    const getSelectedCountryIndices = () => {
        if (!countries || !data.returnOptions?.countries?.length) return [];

        return data.returnOptions.countries
            .map(countryCode => countries.findIndex(c => c.alpha2 === countryCode))
            .filter(index => index !== -1);
    };
        
    const getSelectedCountryNames = () => {
        if (!countries || !data.returnOptions?.countries?.length) return [];

        return data.returnOptions.countries
            .map(countryCode => {
                const country = countries.find(c => c.alpha2 === countryCode);
                return country ? country.name : null;
            })
            .filter(name => name !== null);
    };

    const selectRegion = (regionIndex) => {
        const countryCodes = regions[regionIndex]?.countries || [];

        // map codes to indices
        const selectedIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        // check if currently selected countries match this region
        const currentlySelectedIndices = getSelectedCountryIndices();
        const isSameSelection =
            selectedIndices.length === currentlySelectedIndices.length &&
            selectedIndices.every(index => currentlySelectedIndices.includes(index));

        if (isSameSelection) {
            // same region clicked → deselect all
            handleCountryChange([]);
        } else {
            // different region → replace selection
            handleCountryChange(selectedIndices);
        }
    };

    const isRegionActive = (regionIndex) => {
        const countryCodes = regions[regionIndex]?.countries || [];

        const regionIndices = countryCodes
            .map(code => countries.findIndex(c => c.alpha2 === code))
            .filter(index => index !== -1);

        const currentlySelectedIndices = getSelectedCountryIndices();

        return (
            regionIndices.length === currentlySelectedIndices.length &&
            regionIndices.every(index => currentlySelectedIndices.includes(index))
        );
    };
        
    return (
        <>
            <Box 
                sx={{
                    width:1,
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    gap:7
                }}
            >
                <Box
                    sx={{
                        width:120,
                    }}
                >
                    <Text>{t("name")}</Text>
                </Box>
                
                <ShValidatedInput
                    name="name"
                    label={t("name")}
                    value={data?.name}
                    onChange={handleChange}
                    error={props.errors.name}
                />
            </Box>
            <Box 
                sx={{
                    width:1,
                    paddingTop: 2,
                    display:"flex",
                    alignItems:"center",
                        justifyContent:"space-between",
                    gap:7
                }}
            >
                <Box
                    sx={{
                        width:120,
                    }}
                >
                    <Text>{t("shipper")}</Text>
                </Box>
                
                
                <InputDropdown
                    key={data?.shipperId}
                    name="shipper"
                    displayName="name"
                    changeField="id"
                    valueKey="id"
                    selected={data?.shipperId ?? -1}
                    options={shippers}
                    noSelection={t("choose_shipper")}
                    onChange={(val) => handleChange("shipperId",val)}
                    error={props.errors.shipperId}
                />
            </Box>

            {/* Optional warehouse: shipping screens of other warehouses don't offer this profile */}
            {!props.isReturnProfile && (
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 2,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:120,
                        }}
                    >
                        <Text>{t("warehouse")}</Text>
                    </Box>

                    <InputDropdown
                        name="warehouseId"
                        displayName="name"
                        valueKey="id"
                        selected={data?.warehouseId || 0}
                        options={[{ id: 0, name: t("all_warehouses") }, ...(warehouses?.data ?? [])]}
                        onChange={(val) => handleChange("warehouseId", val)}
                        closeOnSelection
                    />
                </Box>
            )}

            {/* Return Countries - Only show if isReturnProfile is true */}
            {props.isReturnProfile && (

                <SHQuickAction
                    anchorOrigin={{
                        vertical: "center",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "center",
                        horizontal: "left",
                    }}
                    component={
                        <Box sx={{ ml: 0.5 }}>
                            <Subheading sx={{ mt: 0.5 }}>
                                {t("selected_country")}
                            </Subheading>
                            <Box
                                sx={{
                                    mt: 1,
                                    maxHeight: 500,
                                    width: 300,
                                    display: "grid",
                                    gridTemplateColumns:  "1fr 1fr" ,
                                    gap: "12px 16px" ,
                                    mb:2,
                                }}
                            >
                                {getSelectedCountryNames()?.map((option, idx) => (
                                    <Box key={idx}>
                                        <Text>{option}</Text>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    }
                    sx={{
                        modal: {
                            body: {
                                ml: 5,
                            },
                            triangle: {
                                transform: "rotate(-90deg)",
                                top: "calc(50% - 8px)",
                                left: "18px",
                            },
                        },
                    }}
                >
                    {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                        <Box
                            aria-owns={anchorEl ? "simple-popover" : undefined}
                            aria-haspopup="true"
                            onMouseEnter={data.returnOptions?.countries?.length > 0 ? onMouseEnter : null}
                            onMouseLeave={data.returnOptions?.countries?.length > 0 ? onMouseLeave : null}
                            onTouchStart={data.returnOptions?.countries?.length > 0 ? onTouchStart : null}
                            sx={{
                                width:1,
                                paddingTop: 2,
                                display:"flex",
                                alignItems:"flex-start",
                                justifyContent:"space-between",
                                gap:7
                            }}
                        >
                            <Box
                                sx={{
                                    width:120,
                                    height:40,
                                    display:"flex",
                                    alignItems:'center',
                                }}
                            >
                                <Text>
                                    {t("allowed_return_countries")}
                                </Text>
                            </Box>
                            <Box
                                sx={{
                                    width:1,
                                }}
                            >
                                <Box
                                    sx={{
                                        mb:1.5,
                                        display:"flex",
                                        gap:1,
                                        flexDirection:'column',
                                    }}
                                >
                                    {regions.map((region,idx) => (
                                        <ButtonBase
                                            className={cx(undefined, {
                                                'is-active':isRegionActive(idx),
                                            })}
                                            onClick={() => selectRegion(idx)}
                                            sx={{
                                                px:1.5,
                                                height:24,
                                                borderRadius:3,
                                                width:1,
                                                justifyContent:'flex-start',
                                                alignItems:'center',
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                                color:colors.txt['primary'],
                                                fontWeight:500,
                                                fontFamily:"Roboto,sans-serif",

                                                "& .region-icon" : {
                                                    display:"none",
                                                },

                                                "&:hover" : {
                                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[50],
                                                },
                                                "&.is-active" : {
                                                    background:theme.palette.mode=== "dark" ? colors.blue[100] : colors.primary[100],
                                                    borderColor:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                                    color:theme.palette.mode=== "dark" ? colors.blue[400] : colors.primary[400],
                                                    justifyContent:'space-between',
                                                    "& .region-icon" : {
                                                        display:"block",
                                                    },

                                                },

                                            }}
                                        >
                                            {region.name}

                                            <CheckIcon fontSize='small' className="region-icon"/>
                                        </ButtonBase>
                                    ))}
                                </Box>
                                <InputDropdown
                                    // key={`return-countries-${data.returnOptions?.countries?.length || 0}`}
                                    name="returnCountries"
                                    multiple={true}
                                    noSelection={t('choose_countries')}
                                    displayName="name"
                                    searchOptions={{
                                        enabled: true,
                                        placeholder: t("search")
                                    }}
                                    options={countries ?? []}
                                    selected={getSelectedCountryIndices()}
                                    onChange={handleCountryChange}
                                    closeOnSelection={false}
                                />
                            </Box>
                        </Box>
                    )}
                </SHQuickAction>
            )}
        </>
    );
};

const BasicDetails = (props) => {
    const { t } = useTranslation();

    const schema = yup.object({
        name: yupRequired('name',t),
        shipperId: yupRequired('shipper',t),
    });

    return (
        <ProgressionTab
        tabId="BasicDetails"
        subtitle={t('basic_details')}
        step={1}
        nextTab={"tabConfiguration"}
        maxSteps={2}
        schema={schema}
    >
        <Content isReturnProfile={props.isReturnProfile} />
    </ProgressionTab>
    )
}

export default BasicDetails;